import { useState, useRef, useEffect } from 'react';
import {
  Plus,
  X,
  Paperclip,
  FileText,
  BarChart3,
  User,
  Target,
  Briefcase,
  Rocket,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import ArtifactSelectorDialog, {
  type ArtifactAttachment,
} from './ArtifactSelectorDialog';

import { useTranslation } from 'react-i18next';
const artifactIcons: Record<string, typeof BarChart3> = {
  market_analysis: BarChart3,
  persona: User,
  positioning: Target,
  business_model: Briefcase,
  mvp_plan: Rocket,
};

interface MessageInputAreaProps {
  input: string;
  setInput: (value: string) => void;
  attachments: File[];
  setAttachments: React.Dispatch<React.SetStateAction<File[]>>;
  artifactAttachments: ArtifactAttachment[];
  setArtifactAttachments: React.Dispatch<React.SetStateAction<ArtifactAttachment[]>>;
  isLoading: boolean;
  isLimitReached: boolean;
  onSend: () => void;
}

export default function MessageInputArea({
  input,
  setInput,
  attachments,
  setAttachments,
  artifactAttachments,
  setArtifactAttachments,
  isLoading,
  isLimitReached,
  onSend,
}: MessageInputAreaProps) {
  const { t } = useTranslation('chat');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const [showArtifactSelector, setShowArtifactSelector] = useState(false);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  // Close menu on outside click
  useEffect(() => {
    if (!showPlusMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowPlusMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showPlusMenu]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const validFiles = Array.from(files).filter((file) => {
        const ext = file.name.split('.').pop()?.toLowerCase();
        return ['pdf', 'csv', 'png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext || '');
      });
      setAttachments((prev) => [...prev, ...validFiles]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const removeArtifactAttachment = (id: string) => {
    setArtifactAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const hasChips = attachments.length > 0 || artifactAttachments.length > 0;

  return (
    <div className="sticky bottom-0 px-8 py-4 bg-[#F8F8F8]">
      {/* Attachments & artifact chips preview */}
      {hasChips && (
        <div className="flex gap-[8px] mb-[8px] flex-wrap max-w-2xl mx-auto">
          {/* File chips */}
          {attachments.map((file, index) => (
            <div
              key={`file-${index}`}
              className="flex items-center gap-[8px] bg-white rounded-[8px] px-[8px] py-[4px] border border-[#eeeeee]"
            >
              <Paperclip className="h-3.5 w-3.5 text-[#565858] flex-shrink-0" />
              <span className="font-segoe text-[14px] leading-[20px] text-[#0d0d0d] truncate max-w-[150px]">
                {file.name}
              </span>
              <button
                onClick={() => removeAttachment(index)}
                className="text-[#565858] hover:text-[#0d0d0d] transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          {/* Artifact chips */}
          {artifactAttachments.map((artifact) => {
            const Icon = artifactIcons[artifact.type] || FileText;
            return (
              <div
                key={`artifact-${artifact.id}`}
                className="flex items-center gap-[8px] bg-[rgba(33,151,241,0.08)] rounded-[8px] px-[8px] py-[4px] border border-[rgba(33,151,241,0.3)]"
              >
                <Icon className="h-3.5 w-3.5 text-[#2197f1] flex-shrink-0" />
                <span className="font-segoe text-[14px] leading-[20px] text-[#0d0d0d] truncate max-w-[150px]">
                  {artifact.label}
                </span>
                <button
                  onClick={() => removeArtifactAttachment(artifact.id)}
                  className="text-[#565858] hover:text-[#0d0d0d] transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-white rounded-[24px] p-[8px] pl-[16px] flex items-center gap-[8px] max-w-2xl mx-auto border border-[#eeeeee]">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.csv,.png,.jpg,.jpeg,.gif,.webp"
          multiple
          onChange={handleFileSelect}
        />

        {/* Plus button with menu */}
        <div className="relative flex items-center" ref={menuRef}>
          <button
            className="text-[var(--grey-text,#565858)] hover:text-[var(--black-text,#0d0d0d)] flex-shrink-0 transition-colors disabled:opacity-50 flex items-center justify-center"
            onClick={() => setShowPlusMenu(!showPlusMenu)}
            disabled={isLoading || isLimitReached}
          >
            <Plus className="h-5 w-5" />
          </button>

          {showPlusMenu && (
            <div className="absolute bottom-full left-0 mb-[8px] bg-white rounded-[12px] border border-[#eeeeee] shadow-lg overflow-hidden min-w-[200px] z-50">
              <button
                className="w-full flex items-center gap-[12px] px-[16px] py-[12px] hover:bg-[#f4f4f3] transition-colors text-left"
                onClick={() => {
                  setShowPlusMenu(false);
                  fileInputRef.current?.click();
                }}
              >
                <Paperclip className="h-4 w-4 text-[#565858]" />
                <span className="font-segoe text-[14px] leading-[20px] text-[#0d0d0d]">
                  Прикрепить файл
                </span>
              </button>
              <button
                className="w-full flex items-center gap-[12px] px-[16px] py-[12px] hover:bg-[#f4f4f3] transition-colors text-left"
                onClick={() => {
                  setShowPlusMenu(false);
                  setShowArtifactSelector(true);
                }}
              >
                <FileText className="h-4 w-4 text-[#2197f1]" />
                <span className="font-segoe text-[14px] leading-[20px] text-[#0d0d0d]">
                  Прикрепить материал
                </span>
              </button>
            </div>
          )}
        </div>

        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('input.placeholder')}
          className={cn(
            "flex-1 bg-transparent font-segoe text-[16px] leading-[24px] text-[#0d0d0d] placeholder:text-[#969696] outline-none resize-none overflow-hidden px-[8px]",
            'min-h-[24px] max-h-[120px]',
            isLimitReached && 'opacity-50 cursor-not-allowed'
          )}
          disabled={isLoading || isLimitReached}
          rows={1}
        />

        <button
          onClick={onSend}
          disabled={!input.trim() || isLoading || isLimitReached}
          className={cn(
            'p-[8px] rounded-[16px] transition-colors flex-shrink-0',
            input.trim() && !isLoading && !isLimitReached
              ? 'bg-[#2197f1] hover:bg-[#1a7bc7]'
              : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed'
          )}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M7.4 6.32L15.89 3.49C19.7 2.22 21.77 4.3 20.51 8.11L17.68 16.6C15.78 22.31 12.66 22.31 10.76 16.6L9.92 14.08L7.4 13.24C1.69 11.34 1.69 8.23 7.4 6.32Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10.1101 13.6496L13.6901 10.0596" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* Artifact selector dialog */}
      <ArtifactSelectorDialog
        open={showArtifactSelector}
        onOpenChange={setShowArtifactSelector}
        onConfirm={setArtifactAttachments}
        alreadySelected={artifactAttachments}
      />

      <p className="text-center font-segoe text-[12px] leading-[18px] text-[color:var(--grey-text,#565858)] mt-[16px]">
        {t('input.disclaimer')}
      </p>
    </div>
  );
}
